import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useRef } from 'react';
import L from 'leaflet';
import { createBaseLayer, QUEBEC_CENTER } from '@/lib/map';
import { groupMarkerIcon } from '@/lib/map-markers';
import { STATUS_COLORS } from '@/lib/report-colors';
import { HOTSPOT_STYLES } from '@/components/ReportMap';
/** Palette séquentielle (clair → foncé) pour les cartes thématiques */
export const SEQUENTIAL = ['#DBEAFE', '#93C5FD', '#3B82F6', '#1D4ED8', '#1E3A8A'];
/**
 * Seuils par quantiles (5 classes au plus) ; renvoie les bornes supérieures,
 * sans doublon : beaucoup de zones à zéro donnent moins de classes.
 */
export function quantileBreaks(values, classes = 5) {
    const v = values.filter((x) => Number.isFinite(x)).sort((a, b) => a - b);
    if (!v.length)
        return [];
    const raw = Array.from({ length: classes }, (_, i) => v[Math.min(v.length - 1, Math.ceil(((i + 1) * v.length) / classes) - 1)]);
    return [...new Set(raw)];
}
export const classIndex = (value, breaks) => Math.max(0, breaks.findIndex((b) => value <= b));
/** Couleur d'une classe : les classes restantes sont réparties sur toute la palette */
export const classColor = (i, count) => SEQUENTIAL[count <= 1 ? SEQUENTIAL.length - 1 : Math.round((i * (SEQUENTIAL.length - 1)) / (count - 1))];
/**
 * Carte d'analyse de la page Statistiques : carte thématique des quartiers
 * (valeur par zone) ou hexagones des points chauds, et marqueurs de lieux.
 */
export function SpatialMap({ zones, values, breaks, hotspots, points, lines, onZoneClick, className, }) {
    const ref = useRef(null);
    const mapRef = useRef(null);
    const groupRef = useRef(null);
    const clickRef = useRef(onZoneClick);
    clickRef.current = onZoneClick;
    const lastSize = useRef({ w: 0, h: 0 });
    /** Cadre la carte sur ses couches ; rappelé quand le conteneur change de taille */
    const fit = () => {
        const b = groupRef.current?.getBounds();
        if (mapRef.current && b?.isValid())
            mapRef.current.fitBounds(b, { padding: [16, 16], maxZoom: 15 });
    };
    useEffect(() => {
        if (!ref.current || mapRef.current)
            return;
        const map = L.map(ref.current, { attributionControl: false, scrollWheelZoom: false }).setView(QUEBEC_CENTER, 8);
        createBaseLayer().addTo(map);
        groupRef.current = L.featureGroup().addTo(map);
        mapRef.current = map;
        const ro = new ResizeObserver((entries) => {
            const r = entries[0].contentRect;
            map.invalidateSize();
            // Premier affichage ou vrai changement de taille (pas un simple défilement)
            if (Math.abs(r.width - lastSize.current.w) > 2 || Math.abs(r.height - lastSize.current.h) > 2) {
                lastSize.current = { w: r.width, h: r.height };
                fit();
            }
        });
        ro.observe(ref.current);
        return () => { ro.disconnect(); map.remove(); mapRef.current = null; };
    }, []);
    useEffect(() => {
        const map = mapRef.current;
        const group = groupRef.current;
        if (!map || !group)
            return;
        group.clearLayers();
        if (zones?.length) {
            L.geoJSON({ type: 'FeatureCollection', features: zones.filter((z) => z.geometry).map((z) => ({ type: 'Feature', properties: z, geometry: z.geometry })) }, {
                style: (f) => {
                    const z = f?.properties;
                    const v = values?.get(z.id);
                    const thematic = values && breaks?.length && v != null;
                    return thematic
                        ? { color: '#1E293B', weight: 1, fillColor: classColor(classIndex(v, breaks), breaks.length), fillOpacity: 0.7 }
                        : { color: '#1D4ED8', weight: 1.5, dashArray: '5 4', fillOpacity: 0.02 };
                },
                onEachFeature: (f, layer) => {
                    const z = f.properties;
                    const v = values?.get(z.id);
                    layer.bindTooltip(`<strong>${z.name}</strong>${v != null ? `<br>${v.toLocaleString('fr-CA')}` : ''}`, { sticky: true });
                    layer.on('click', () => clickRef.current?.(z.id));
                },
            }).addTo(group);
        }
        if (hotspots?.cells.length) {
            L.geoJSON({ type: 'FeatureCollection', features: hotspots.cells.map((c) => ({ type: 'Feature', properties: c, geometry: c.geometry })) }, {
                style: (f) => {
                    const s = HOTSPOT_STYLES[(f?.properties).cls] ?? HOTSPOT_STYLES.none;
                    return { color: s.color, weight: 0.6, fillColor: s.color, fillOpacity: s.opacity };
                },
                onEachFeature: (f, layer) => {
                    const c = f.properties;
                    layer.bindTooltip(`${c.n} signalement(s) · ${HOTSPOT_STYLES[c.cls]?.label} (z = ${c.z})`, { sticky: true });
                },
            }).addTo(group);
        }
        if (lines?.length) {
            const lb = quantileBreaks(lines.map((l) => l.value));
            for (const l of lines) {
                const i = classIndex(l.value, lb);
                L.geoJSON({ type: 'Feature', properties: {}, geometry: l.geometry }, {
                    style: { color: classColor(i, lb.length), weight: 3 + i * 1.5, opacity: 0.95 },
                }).bindTooltip(l.label, { sticky: true }).addTo(group);
            }
        }
        for (const p of points ?? []) {
            L.marker([p.latitude, p.longitude], { icon: groupMarkerIcon(p.weight ?? 1, STATUS_COLORS.OPEN) }).bindTooltip(p.label).addTo(group);
        }
        fit();
    }, [zones, values, breaks, hotspots, points, lines]); // eslint-disable-line react-hooks/exhaustive-deps
    return _jsx("div", { ref: ref, className: className });
}
//# sourceMappingURL=SpatialMap.js.map